import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoading } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { fmtDate, RAG } from "@/lib/format";
import { DeliveryPanel, deliveryPrefill } from "@/components/workspace/DeliveryPanel";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const MONDAY_Q = [
  { key: "priorities", label: "What are your three most important priorities this week?" },
  { key: "by_friday", label: "What must be completed by Friday?" },
  { key: "blocked", label: "What is currently blocked?" },
  { key: "decisions", label: "What decisions are required?" },
  { key: "support", label: "Where do you need support from Amden?" },
];
const FRIDAY_Q = [
  { key: "completed", label: "What did you complete this week?" },
  { key: "not_completed", label: "What was not completed?" },
  { key: "why", label: "Why was it not completed?" },
  { key: "best_result", label: "What was the most important result?" },
  { key: "learned", label: "What did you learn?" },
  { key: "carry_forward", label: "What must carry forward into next week?" },
];

function Confidence({ value, onChange, label }) {
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {Array.from({ length: 10 }).map((_, i) => {
          const n = i + 1;
          const active = value === n;
          const tone = n <= 3 ? "#C95555" : n <= 6 ? "#D99A32" : "#3D8B65";
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              data-testid={`confidence-${n}`}
              className="flex h-9 w-9 items-center justify-center rounded-lg border text-sm font-semibold transition-all"
              style={{
                borderColor: active ? tone : "#E4E8EF",
                background: active ? tone : "#fff",
                color: active ? "#fff" : "#1E2D44",
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CheckinForm({ type, questions, orgId, prefill, onDone }) {
  const [answers, setAnswers] = useState(prefill || {});
  const [score, setScore] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!score) return toast.error("Please rate your confidence / week from 1–10");
    setSaving(true);
    try {
      await api.post(`/checkins?org_id=${orgId}`, { type, answers, score });
      toast.success("Check-in saved");
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-4">
      {questions.map((q) => (
        <div key={q.key}>
          <Label className="text-sm">{q.label}</Label>
          <Textarea
            value={answers[q.key] || ""}
            onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
            className="mt-1.5"
            rows={2}
            data-testid={`checkin-${type}-${q.key}`}
          />
        </div>
      ))}
      <Confidence
        value={score}
        onChange={setScore}
        label={type === "monday" ? "How confident are you about this week's objectives? (1–10)" : "How would you rate the week? (1–10)"}
      />
      <Button onClick={submit} disabled={saving} className="w-full bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid={`submit-${type}-checkin`}>
        {saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Submit check-in"}
      </Button>
    </div>
  );
}

export default function WeeklyFocus() {
  const { orgId } = useOrg();
  const { canEdit, isAmden } = useAuth();
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState(null);
  const [active, setActive] = useState(null); // 'monday' | 'friday'

  const load = () => {
    api.get(`/checkins/current?org_id=${orgId}`).then((r) => setCurrent(r.data));
    api.get(`/checkins?org_id=${orgId}`).then((r) => setHistory(r.data));
  };
  useEffect(() => { if (orgId) { setCurrent(null); setHistory(null); load(); } }, [orgId]);

  if (!current || !history) return <PageLoading />;
  const prefill = deliveryPrefill(current.delivery);

  const trend = [...history].filter((c) => c.score).reverse().slice(-10).map((c) => ({
    name: `${c.type === "monday" ? "Mon" : "Fri"} ${fmtDate(c.created_at).slice(0, 5)}`,
    score: c.score,
  }));

  const completionInsight = () => {
    const fridays = history.filter((c) => c.type === "friday" && c.score);
    if (!fridays.length) return null;
    const avg = (fridays.reduce((s, c) => s + c.score, 0) / fridays.length).toFixed(1);
    return avg;
  };

  return (
    <div data-testid="weekly-focus-page">
      <PageHeader
        eyebrow="Rhythm"
        title="Weekly Focus"
        subtitle="A quick Monday plan and Friday reflection keeps the whole business accountable."
        icon="CalendarCheck"
      />

      {current.delivery && !isAmden && <DeliveryPanel d={current.delivery} />}

      {/* This week */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="amden-card p-6" data-testid="monday-checkin-card">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#4A78A8]/10 text-[#4A78A8]"><Icons.CalendarPlus size={18} /></span>
              <div>
                <h3 className="font-display text-base font-bold">Monday Planning</h3>
                <p className="text-xs text-[#1E2D44]/50">Set the week up for success</p>
              </div>
            </div>
            {current.monday ? <RagBadgeInline rag="green">Done</RagBadgeInline> : <RagBadgeInline rag="amber">Due</RagBadgeInline>}
          </div>
          {current.monday ? (
            <div className="rounded-lg bg-[#F4F6F9] p-4 text-sm text-[#1E2D44]/70">
              <div className="mb-2 font-medium text-[#0B1729]">Confidence: {current.monday.score}/10</div>
              <p className="line-clamp-3">{current.monday.answers?.priorities}</p>
            </div>
          ) : canEdit ? (
            active === "monday" ? (
              <CheckinForm type="monday" questions={MONDAY_Q} orgId={orgId} prefill={prefill.monday} onDone={() => { setActive(null); load(); }} />
            ) : (
              <Button onClick={() => setActive("monday")} className="w-full bg-[#4A78A8] hover:bg-[#3f6791]" data-testid="start-monday-checkin">Start Monday check-in</Button>
            )
          ) : <p className="text-sm text-[#1E2D44]/50">Read-only access.</p>}
        </div>

        <div className="amden-card p-6" data-testid="friday-checkin-card">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#3D8B65]/10 text-[#3D8B65]"><Icons.CalendarCheck size={18} /></span>
              <div>
                <h3 className="font-display text-base font-bold">Friday Reflection</h3>
                <p className="text-xs text-[#1E2D44]/50">Review, learn, carry forward</p>
              </div>
            </div>
            {current.friday ? <RagBadgeInline rag="green">Done</RagBadgeInline> : <RagBadgeInline rag="grey">Open</RagBadgeInline>}
          </div>
          {current.friday ? (
            <div className="rounded-lg bg-[#F4F6F9] p-4 text-sm text-[#1E2D44]/70">
              <div className="mb-2 font-medium text-[#0B1729]">Week rated: {current.friday.score}/10</div>
              <p className="line-clamp-3">{current.friday.answers?.best_result}</p>
            </div>
          ) : canEdit ? (
            active === "friday" ? (
              <CheckinForm type="friday" questions={FRIDAY_Q} orgId={orgId} prefill={prefill.friday} onDone={() => { setActive(null); load(); }} />
            ) : (
              <Button onClick={() => setActive("friday")} className="w-full bg-[#3D8B65] hover:bg-[#347556]" data-testid="start-friday-checkin">Start Friday reflection</Button>
            )
          ) : <p className="text-sm text-[#1E2D44]/50">Read-only access.</p>}
        </div>
      </div>

      {/* Trends */}
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="amden-card p-6 lg:col-span-2">
          <h3 className="mb-4 font-display text-base font-bold">Confidence & week rating trend</h3>
          {trend.length > 1 ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F6" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#8A97AB" }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 10]} tick={{ fontSize: 11, fill: "#8A97AB" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E4E8EF", fontSize: 12 }} />
                  <Line type="monotone" dataKey="score" stroke="#FFB000" strokeWidth={2.5} dot={{ r: 3, fill: "#0B1729" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyState icon="TrendingUp" title="Not enough data" description="Complete a few check-ins to see trends." />}
        </div>
        <div className="amden-card p-6">
          <h3 className="mb-4 font-display text-base font-bold">Insights</h3>
          <div className="space-y-3 text-sm">
            <div className="rounded-lg bg-[#F4F6F9] p-3">
              <div className="label-eyebrow mb-1">Average week rating</div>
              <div className="font-display text-2xl font-bold">{completionInsight() || "—"}<span className="text-sm text-[#1E2D44]/40">/10</span></div>
            </div>
            <div className="rounded-lg bg-[#F4F6F9] p-3">
              <div className="label-eyebrow mb-1">Check-ins logged</div>
              <div className="font-display text-2xl font-bold">{history.length}</div>
            </div>
          </div>
        </div>
      </div>

      {/* History */}
      <div className="mt-6">
        <h3 className="mb-3 font-display text-base font-bold">Check-in history</h3>
        {history.length === 0 ? (
          <EmptyState icon="History" title="No check-ins yet" description="Your first check-in will appear here." />
        ) : (
          <div className="space-y-2.5">
            {history.map((c) => (
              <div key={c.id} className="amden-card p-4" data-testid={`checkin-history-${c.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${c.type === "monday" ? "bg-[#4A78A8]/10 text-[#4A78A8]" : "bg-[#3D8B65]/10 text-[#3D8B65]"}`}>
                      {c.type === "monday" ? <Icons.CalendarPlus size={15} /> : <Icons.CalendarCheck size={15} />}
                    </span>
                    <div>
                      <div className="text-sm font-semibold capitalize text-[#0B1729]">{c.type} check-in</div>
                      <div className="text-xs text-[#1E2D44]/50">{c.user_name} · {fmtDate(c.created_at)}</div>
                    </div>
                  </div>
                  <span className="font-display text-lg font-bold" style={{ color: c.score <= 3 ? "#C95555" : c.score <= 6 ? "#D99A32" : "#3D8B65" }}>{c.score}/10</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RagBadgeInline({ rag, children }) {
  return <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${RAG[rag].bg} ${RAG[rag].text}`}><span className={`h-1.5 w-1.5 rounded-full ${RAG[rag].dot}`} />{children}</span>;
}
